import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";

import { candidatosConvite } from "services/bidFreteApi";
import { FILTRO_CANDIDATO, ROTULO_REDE, TITULO_SELO_REDE, agruparCandidatos, coberturaEmail, ehCandidatoRede, emailValido, filtrarCandidatos, temEmailCotacao } from "../../Leiloes/regras";
import { erroDaApi, fmtCnpj, montarParticipantes, soDigitos, validarManual } from "../regras";

// Seletor de convidadas do BID (H7 PO1-2847), no molde do seletor do
// NovoLeilaoModal (carteira + Sourcing, busca, filtros, e-mail digitado para
// quem não tem contato de Cotação) + CNPJ manual. A lista vem de
// GET /gobid/bids/:id/candidatos-convite; quem já é participante fica de fora.
// Controlado: o pai recebe a lista pronta para POST /participantes em `onChange`.

function Selo({ tone, children, title }) {
  return (
    <span className={`lf-badge lf-badge-${tone}`} title={title}>
      {children}
    </span>
  );
}

const FILTROS = [
  { chave: FILTRO_CANDIDATO.todas, rotulo: "Todas" },
  { chave: FILTRO_CANDIDATO.carteira, rotulo: "Carteira" },
  { chave: FILTRO_CANDIDATO.comEmail, rotulo: "Com e-mail" },
  { chave: FILTRO_CANDIDATO.semEmail, rotulo: "Sem e-mail" },
];

const MANUAL_VAZIO = { cnpj: "", email: "", nome: "" };

export default function SeletorConvidados({ empresaId, bidId, excluirIds, excluirCnpjs, onChange }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [selecionados, setSelecionados] = useState({});
  const [emails, setEmails] = useState({});
  const [manuais, setManuais] = useState([]);
  const [manual, setManual] = useState(MANUAL_VAZIO);
  const [errosManual, setErrosManual] = useState({});
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState(FILTRO_CANDIDATO.todas);

  useEffect(() => {
    if (!empresaId || !bidId) return undefined;
    let ativo = true;
    setLoading(true);
    setErro(null);
    candidatosConvite(empresaId, bidId)
      .then((r) => {
        if (ativo) setDados(r || {});
      })
      .catch((err) => {
        if (ativo) setErro(erroDaApi(err, "Não foi possível carregar as transportadoras.").mensagem);
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [empresaId, bidId]);

  // Chave em texto: o pai passa um array novo a cada render, e depender dele
  // faria o onChange abaixo rodar em loop.
  const chaveFora = (excluirIds || []).map(String).join(",");
  const fora = useMemo(() => {
    const ids = {};
    chaveFora.split(",").forEach((i) => {
      if (i) ids[i] = true;
    });
    return ids;
  }, [chaveFora]);
  const candidatos = useMemo(() => {
    const lista = (dados && (dados.candidatos || dados.items)) || (Array.isArray(dados) ? dados : []);
    return lista.filter((c) => !fora[String(c.idTransportadora)]);
  }, [dados, fora]);
  const visiveis = useMemo(() => filtrarCandidatos(candidatos, busca, filtro), [candidatos, busca, filtro]);
  const grupos = useMemo(() => agruparCandidatos(visiveis), [visiveis]);
  const cobertura = useMemo(() => coberturaEmail(candidatos), [candidatos]);
  const maximo = (dados && dados.maximoConvidados) || null;

  // Avisa o pai a cada mudança (lista pronta + pendências de e-mail).
  useEffect(() => {
    if (!onChange) return;
    const participantes = montarParticipantes(candidatos, selecionados, emails, manuais);
    const semEmail = candidatos.filter((c) => selecionados[c.idTransportadora] && !temEmailCotacao(c) && !emailValido(emails[c.idTransportadora])).map((c) => c.nome);
    onChange({ participantes, semEmail });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatos, selecionados, emails, manuais]);

  const toggle = (id) => setSelecionados((cur) => ({ ...cur, [id]: !cur[id] }));
  const marcarComEmail = () =>
    setSelecionados((cur) => {
      const novo = { ...cur };
      visiveis.forEach((c) => {
        if (temEmailCotacao(c)) novo[c.idTransportadora] = true;
      });
      return novo;
    });

  const incluirManual = () => {
    const existentes = manuais.concat((excluirCnpjs || []).map((c) => ({ cnpj: c })));
    const e = validarManual(manual, existentes);
    setErrosManual(e);
    if (Object.keys(e).length) return;
    setManuais((cur) => cur.concat([{ cnpj: soDigitos(manual.cnpj), email: manual.email.trim(), nome: manual.nome.trim() }]));
    setManual(MANUAL_VAZIO);
  };

  const qtdSel = Object.keys(selecionados).filter((k) => selecionados[k]).length + manuais.length;

  const linha = (c) => {
    const sel = Boolean(selecionados[c.idTransportadora]);
    const temEmail = temEmailCotacao(c);
    const digitado = emails[c.idTransportadora] || "";
    const digitadoOk = emailValido(digitado);
    return (
      <div key={c.idTransportadora} className={`lf-row lf-cand${sel ? " is-selected" : ""}`}>
        <label className="lf-cand-main">
          <input type="checkbox" className="lf-cand-check" checked={sel} onChange={() => toggle(c.idTransportadora)} />
          <span className="lf-row-main">
            <span className="lf-row-nome">
              {c.nome}
              {c.naCarteira ? <Selo tone="info">Carteira</Selo> : <Selo tone="neutral" title="Encontrada pela Sourcing; sem vínculo com você">Fora da carteira</Selo>}
              {/* PO1-3648: candidato da Rede Everlog (cobertura declarada, origem REDE). */}
              {ehCandidatoRede(c) ? <Selo tone="warning" title={TITULO_SELO_REDE}>{ROTULO_REDE}</Selo> : null}
              {c.atendeDestino && !ehCandidatoRede(c) ? <Selo tone="success">Atende o destino</Selo> : null}
              {c.temLogin === false ? (
                <Selo tone="warning" title="Recebe o convite de cadastro do Hub no mesmo e-mail">
                  Sem login no MeuFrete
                </Selo>
              ) : null}
            </span>
            <span className="lf-cand-sub">
              {c.cnpj ? <span>{fmtCnpj(c.cnpj)}</span> : null}
              {temEmail ? (
                <span className="lf-cand-email">{c.emails.join(", ")}</span>
              ) : sel && digitadoOk ? (
                <span className="lf-text-success">Novo contato de Cotação</span>
              ) : (
                <span className="lf-text-warning">Sem e-mail de Cotação</span>
              )}
            </span>
          </span>
        </label>
        {!temEmail && sel ? (
          <div className="lf-cand-digitar">
            <Input
              bsSize="sm"
              type="email"
              placeholder="E-mail da transportadora"
              value={digitado}
              invalid={Boolean(digitado) && !digitadoOk}
              onChange={(e) => {
                // React 16 recicla o evento: ler o valor ANTES do updater.
                const valor = e.target.value;
                setEmails((cur) => ({ ...cur, [c.idTransportadora]: valor }));
              }}
            />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="bf-seletor" data-testid="bf-seletor-convidados">
      <div className="lf-section-head">
        <div className="lf-section-title">
          Convidadas · {qtdSel}
          {maximo ? ` de ${maximo}` : ""}
        </div>
        <div className="lf-acoes-lista">
          <button type="button" className="lf-link" onClick={marcarComEmail}>
            marcar as com e-mail
          </button>
        </div>
      </div>

      {loading ? (
        <div className="lf-empty-state">
          <Spinner size="sm" /> Carregando a carteira e a Sourcing...
        </div>
      ) : erro ? (
        <div className="lf-aviso lf-aviso-danger">{erro}</div>
      ) : (
        <>
          {cobertura.semEmail > 0 ? (
            <div className="lf-aviso lf-aviso-warning">
              {cobertura.semEmail} de {cobertura.total} transportadoras estão sem e-mail. Marque e digite o e-mail para convidar.
            </div>
          ) : null}
          {dados && dados.sourcingDisponivel === false ? <div className="lf-aviso lf-aviso-info">Sourcing indisponível para este destino; só a sua carteira aparece.</div> : null}
          {candidatos.length === 0 ? (
            <div className="lf-empty-state">Nenhuma transportadora na carteira nem na Sourcing para o destino do BID.</div>
          ) : (
            <>
              <div className="lf-toolbar">
                <Input
                  bsSize="sm"
                  className="lf-busca"
                  placeholder="Buscar por nome, CNPJ ou e-mail"
                  value={busca}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBusca(v);
                  }}
                />
                <div className="lf-presets lf-filtros">
                  {FILTROS.map((f) => (
                    <button key={f.chave} type="button" className={`lf-preset${filtro === f.chave ? " is-active" : ""}`} onClick={() => setFiltro(f.chave)}>
                      {f.rotulo}
                    </button>
                  ))}
                </div>
              </div>
              <div className="lf-cands">
                {grupos.length === 0 ? <div className="lf-vazio">Nenhuma transportadora com esse filtro.</div> : null}
                {grupos.map((g) => (
                  <div key={g.chave} className="lf-grupo">
                    <div className="lf-grupo-titulo">
                      {g.titulo} <span className="lf-muted">({g.itens.length})</span>
                    </div>
                    <div className="lf-rows">{g.itens.map(linha)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <div className="lf-section" style={{ marginTop: 12 }}>
        <div className="lf-section-title">Transportadora fora da lista (CNPJ)</div>
        <div className="bf-manual">
          <Input
            bsSize="sm"
            placeholder="CNPJ"
            value={manual.cnpj}
            invalid={Boolean(errosManual.cnpj)}
            aria-label="CNPJ manual"
            onChange={(e) => {
              const v = e.target.value;
              setManual((m) => ({ ...m, cnpj: v }));
            }}
          />
          <Input
            bsSize="sm"
            placeholder="Razão social (opcional)"
            value={manual.nome}
            aria-label="Razão social manual"
            onChange={(e) => {
              const v = e.target.value;
              setManual((m) => ({ ...m, nome: v }));
            }}
          />
          <Input
            bsSize="sm"
            type="email"
            placeholder="E-mail"
            value={manual.email}
            invalid={Boolean(errosManual.email)}
            aria-label="E-mail manual"
            onChange={(e) => {
              const v = e.target.value;
              setManual((m) => ({ ...m, email: v }));
            }}
          />
          <Button color="info" size="sm" outline onClick={incluirManual}>
            Incluir
          </Button>
        </div>
        {errosManual.cnpj || errosManual.email ? <div className="lf-erro-campo">{[errosManual.cnpj, errosManual.email].filter(Boolean).join(" ")}</div> : null}
        <div className="lf-muted">Sem login no MeuFrete, ela recebe no e-mail do BID o convite de cadastro do Hub; ao aceitar, fica vinculada a você.</div>
        {manuais.length ? (
          <div className="lf-rows" style={{ marginTop: 8 }}>
            {manuais.map((m) => (
              <div key={m.cnpj} className="lf-row lf-cand is-selected">
                <span className="lf-row-main">
                  <span className="lf-row-nome">
                    {m.nome || fmtCnpj(m.cnpj)} <Selo tone="neutral">Manual</Selo>
                  </span>
                  <span className="lf-cand-sub">
                    {fmtCnpj(m.cnpj)} · {m.email}
                  </span>
                </span>
                <button type="button" className="lf-link" onClick={() => setManuais((cur) => cur.filter((x) => x.cnpj !== m.cnpj))}>
                  remover
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
